import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';
import { BatchStatusResponse } from '../../types/results.types';

interface StateWithDMUnlock {
  dmUnlock: DMUnlockState;
}

export type BatchKind = 'row' | 'bulk';

export interface ActiveBatch {
  batchId: string;
  kind: BatchKind;
  searchId: number | null;
  personIds: number[];
  total?: number;
  progress: number;
  batchStatus: BatchStatusResponse | null;
  startedAt: number;
}

export interface StartBatchPayload {
  batchId: string;
  kind?: BatchKind;
  searchId: number | null;
  personIds: number[];
  total?: number;
}

interface DMUnlockState {
  activeBatches: Record<string, ActiveBatch>;
  recentCompletions: BatchStatusResponse[];
  recentFailures: { batchId: string | null }[];
  modalOwnedBatchId: string | null;
}

const MAX_RECENT = 5;

const initialState: DMUnlockState = {
  activeBatches: {},
  recentCompletions: [],
  recentFailures: [],
  modalOwnedBatchId: null,
};

const dmUnlockSlice = createSlice({
  name: 'dmUnlock',
  initialState,
  reducers: {
    startBatch: (state, action: PayloadAction<StartBatchPayload>) => {
      const { batchId, kind = 'bulk', searchId, personIds, total } = action.payload;
      state.activeBatches[batchId] = {
        batchId,
        kind,
        searchId,
        personIds,
        total,
        progress: 0,
        batchStatus: null,
        startedAt: Date.now(),
      };
    },

    updateProgress: (state, action: PayloadAction<BatchStatusResponse>) => {
      const batch = state.activeBatches[action.payload.batch_id];
      if (batch) {
        batch.batchStatus = action.payload;
        batch.progress = action.payload.progress_percent;
      }
    },

    setCompleted: (state, action: PayloadAction<BatchStatusResponse>) => {
      delete state.activeBatches[action.payload.batch_id];
      state.recentCompletions = [
        action.payload,
        ...state.recentCompletions,
      ].slice(0, MAX_RECENT);
    },

    setFailed: (state, action: PayloadAction<string | null>) => {
      if (action.payload) {
        delete state.activeBatches[action.payload];
      }
      state.recentFailures = [
        { batchId: action.payload },
        ...state.recentFailures,
      ].slice(0, MAX_RECENT);
    },

    resetBatch: (state, action: PayloadAction<string>) => {
      delete state.activeBatches[action.payload];
    },

    resetUnlock: (state) => {
      state.activeBatches = {};
      state.recentCompletions = [];
      state.recentFailures = [];
    },

    clearRecentCompletion: (state, action: PayloadAction<string>) => {
      state.recentCompletions = state.recentCompletions.filter(
        (c) => c.batch_id !== action.payload,
      );
    },

    clearRecentFailure: (state, action: PayloadAction<string | null>) => {
      state.recentFailures = state.recentFailures.filter(
        (f) => f.batchId !== action.payload,
      );
    },

    setModalOwnedBatchId: (state, action: PayloadAction<string | null>) => {
      state.modalOwnedBatchId = action.payload;
    },

    // Legacy aliases used by DMUnlockCompletionToast — operate on head of queue.
    clearCompletedStatus: (state) => {
      state.recentCompletions = state.recentCompletions.slice(1);
    },
    clearFailedStatus: (state) => {
      state.recentFailures = state.recentFailures.slice(1);
    },
  },
});

export const {
  startBatch,
  updateProgress,
  setCompleted,
  setFailed,
  resetBatch,
  resetUnlock,
  clearRecentCompletion,
  clearRecentFailure,
  clearCompletedStatus,
  clearFailedStatus,
  setModalOwnedBatchId,
} = dmUnlockSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

const selectDMUnlock = (state: StateWithDMUnlock) => state.dmUnlock;

export const selectIsAnyDMPolling = createSelector(
  selectDMUnlock,
  (s) => Object.keys(s.activeBatches).length > 0,
);

export const selectIsBulkDMPolling = createSelector(
  selectDMUnlock,
  (s) => Object.values(s.activeBatches).some((b) => b.kind === 'bulk'),
);

export const selectAggregateRemainingCount = createSelector(
  selectDMUnlock,
  (s) => {
    const batches = Object.values(s.activeBatches);
    if (batches.length === 0) return 0;
    return batches.reduce((sum, b) => {
      if (!b.batchStatus) return sum + (b.total ?? b.personIds.length);
      return sum + (b.batchStatus.pending + b.batchStatus.processing);
    }, 0);
  },
);

export const selectActiveBulkBatch = createSelector(
  selectDMUnlock,
  (s) => {
    const bulkBatches = Object.values(s.activeBatches)
      .filter((b) => b.kind === 'bulk')
      .sort((a, b) => b.startedAt - a.startedAt);
    return bulkBatches[0] ?? null;
  },
);

export const selectAllActivePersonIds = createSelector(
  selectDMUnlock,
  (s) => {
    const seen = new Set<number>();
    for (const batch of Object.values(s.activeBatches)) {
      for (const id of batch.personIds) seen.add(id);
    }
    return Array.from(seen);
  },
);

// Convenience: head of queue for toast display
export const selectHeadCompletion = createSelector(
  selectDMUnlock,
  (s) => s.recentCompletions[0] ?? null,
);

export const selectHeadCompletionForGlobalUi = createSelector(
  selectDMUnlock,
  (state) => {
    const headCompletion = state.recentCompletions[0];
    if (!headCompletion) {
      return null;
    }
    if (state.modalOwnedBatchId && headCompletion.batch_id === state.modalOwnedBatchId) {
      return null;
    }
    return headCompletion;
  },
);

export const selectHeadFailure = createSelector(
  selectDMUnlock,
  (s) => s.recentFailures[0] ?? null,
);

export default dmUnlockSlice.reducer;
